import React, {Component} from 'react';
import DefaultLayout from 'layouts/Default';
import Form, {FormStatus} from 'components/forms/Form';
import './index.scss';
import {ValidationGroup, Validators} from 'util/validation';
import TextField from 'components/forms/TextField';
import {Button, MenuItem} from '@material-ui/core';
import ProgressSpinner from 'components/ProgressSpinner';
import SelectField from 'components/forms/SelectField';
import {SupportRequest, SupportRequestType} from 'services/entities/contact';
import ImageUploadField from 'components/forms/ImageUploadField';
import {requestSupport} from 'services/contact';
import {handleError} from 'util/errors';
import PhoneNumberField from 'components/forms/PhoneNumberField';

interface SupportFormState {
  status: FormStatus;
}

export default class SupportPage extends Component<{}, SupportFormState> {
  private readonly validation: ValidationGroup;
  private readonly model: SupportRequest;
  public state = {
    status: FormStatus.None
  };

  constructor(props: any) {
    super(props);
    this.model = {
      firstName: '',
      lastName: null,
      email: '',
      phoneNumber: null,
      type: null,
      summary: '',
      description: '',
      attachment: null
    };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("firstName", { required: Validators.required });
    this.validation.addField("email", {
      required: Validators.required,
      email: Validators.email
    });
    this.validation.addField("phoneNumber", { format: Validators.phone });
    this.validation.addField("type", { required: Validators.required });
    this.validation.addField("summary", { required: Validators.required });
    this.validation.addField("description", { required: Validators.required });
    this.validation.addField("attachment", { maxSize: Validators.maxFileSize(2 * 1024 * 1024) });
  }

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    requestSupport(this.model).then(
      () => this.setState({ status: FormStatus.Success }),
      ex => {
        handleError('Support request failed', ex);
        this.setState({ status: FormStatus.None });
      }
    );
  };

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  renderForm() {
    const disabled = this.state.status === FormStatus.Submitting || this.state.status === FormStatus.Success;
    return <>
        <h2>Support</h2>
        <Form onSubmit={this.onSubmit} validationGroup={this.validation}>
          <TextField
            name="firstName"
            label="First name"
            type="text"
            className="first-name"
            required={true}
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{ required: "First name is required" }}
            disabled={disabled}
            onInput={this.onFieldInput}
          />
          <TextField
            name="lastName"
            label="Last name"
            type="text"
            className="last-name"
            validationGroup={this.validation}
            model={this.model}
            disabled={disabled}
            onInput={this.onFieldInput}
          />
          <TextField
            name="email"
            label="Email"
            type="text"
            required={true}
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{ required: "Email is required", email: 'Invalid email' }}
            disabled={disabled}
            onInput={this.onFieldInput}
          />
          <PhoneNumberField
            name="phoneNumber"
            label="Phone number"
            className="phone-number"
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{ format: "Invalid phone number" }}
            disabled={disabled}
            onInput={this.onFieldInput}
          />
          <SelectField
            name="type"
            label="I need help with..."
            className="type"
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{ required: "Support type is required" }}
            disabled={disabled}
            onChange={this.onFieldInput}>
            <MenuItem value={SupportRequestType.Booking}>Booking a Sook</MenuItem>
            <MenuItem value={SupportRequestType.Designing}>Designing a Sook</MenuItem>
            <MenuItem value={SupportRequestType.Other}>Something else...</MenuItem>
          </SelectField>
          <TextField
            name="summary"
            label="What is the summary of the problem"
            placeholder="e.g. I am having trouble with my booking for 4pm in Oxford Street on 14/02/2021"
            type="text"
            className="summary"
            required={true}
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{ required: "Summary is required" }}
            disabled={disabled}
            onInput={this.onFieldInput}
          />
          <TextField
            name="description"
            label="Description"
            placeholder="e.g. I am having trouble with my booking called &quot;Dans Doggo's&quot;"
            type="text"
            className="description"
            multiline={true}
            required={true}
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{ required: "Description is required" }}
            disabled={disabled}
            onInput={this.onFieldInput}
          />
          <ImageUploadField
            name="attachment"
            label="Screen shot"
            className="attachment"
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{ maxSize: "Screen shot must be less than 2MB" }}
            disabled={disabled}
            onInput={this.onFieldInput}
          />
          <section className="actions">
            <Button className="submit" type="submit" disabled={disabled}>
              {this.state.status === FormStatus.Submitting && <ProgressSpinner />}
              Submit
            </Button>
          </section>
        </Form>
      </>;
  }

  render() {
    return <DefaultLayout id="support-page">
      { this.state.status !== FormStatus.Success && this.renderForm() }
      { this.state.status === FormStatus.Success &&
        <div className="success">
          <h2>Your request has been sent</h2>
          <p>Someone will be in contact with you shortly.</p>
        </div>
      }
    </DefaultLayout>;
  }
}